import React from 'react';

import { cssMap, cx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

const COLOR_VARIANTS = [
	'blue',
	'purple',
	'green',
	'teal',
	'orange',
	'magenta',
	'lime',
	'red',
] as const;

type ColorVariant = (typeof COLOR_VARIANTS)[number];

const styles = cssMap({
	container: {
		alignItems: 'center',
		borderRadius: token('radius.medium'),
		display: 'flex',
		font: token('font.body.small'),
		height: '100%',
		justifyContent: 'center',
		width: '100%',
	},
	blue: {
		backgroundColor: token('color.background.accent.blue.subtler'),
		color: token('color.text.accent.blue.bolder'),
	},
	purple: {
		backgroundColor: token('color.background.accent.purple.subtler'),
		color: token('color.text.accent.purple.bolder'),
	},
	green: {
		backgroundColor: token('color.background.accent.green.subtler'),
		color: token('color.text.accent.green.bolder'),
	},
	teal: {
		backgroundColor: token('color.background.accent.teal.subtler'),
		color: token('color.text.accent.teal.bolder'),
	},
	orange: {
		backgroundColor: token('color.background.accent.orange.subtler'),
		color: token('color.text.accent.orange.bolder'),
	},
	magenta: {
		backgroundColor: token('color.background.accent.magenta.subtler'),
		color: token('color.text.accent.magenta.bolder'),
	},
	lime: {
		backgroundColor: token('color.background.accent.lime.subtler'),
		color: token('color.text.accent.lime.bolder'),
	},
	red: {
		backgroundColor: token('color.background.accent.red.subtler'),
		color: token('color.text.accent.red.bolder'),
	},
});

// Mirrors the stable name-to-variant approach used by @atlassian/unit-tile.
export const getLoomSpaceColorVariant = (name: string): ColorVariant => {
	const characterCodeTotal = name
		.split('')
		.reduce((total, character) => total + character.charCodeAt(0), 0);

	return COLOR_VARIANTS[characterCodeTotal % COLOR_VARIANTS.length];
};

export const LoomSpaceIcon = ({ name }: { name: string }): React.JSX.Element => {
	const colorVariant = getLoomSpaceColorVariant(name);
	const initial = name.trim().charAt(0).toUpperCase();

	return (
		<Box
			aria-hidden="true"
			data-color-variant={colorVariant}
			testId="loom-space-icon"
			xcss={cx(styles.container, styles[colorVariant])}
		>
			{initial}
		</Box>
	);
};
